import { useState } from 'react'
import { Box, Button, Flex, Icon, Typography } from '@percent/lemonade'
import { useTranslation } from 'react-i18next'
import {
  POSTHOG_FEATURE_FLAGS,
  useAuthState,
  useCausesPortalAnalytics,
  usePostHogFeatureFlag
} from '@percent/cause-dashboard/common/hooks'
import { GrantAdvisoryBookingModal } from '@percent/cause-dashboard/common/components/grantAdvisoryBookingModal'
import { GRANT_ADVISORY_BOOKING_TRACKING } from '@percent/cause-dashboard/common/components/grantAdvisoryBookingModal/grantAdvisoryBooking.utils'
import { useGrantAdvisoryBookingUrl } from '@percent/cause-dashboard/common/components/grantAdvisoryBookingModal/useGrantAdvisoryBookingUrl'
import { SIDEBAR_GOODSTACK_PRO_PROMO_FORM_LINK } from '@percent/cause-dashboard/constants/support'
import styles from './SidebarGrantPromo.module.scss'

export const SidebarGrantPromo = () => {
  const { t } = useTranslation()
  const { authState } = useAuthState()
  const { track } = useCausesPortalAnalytics()
  const isBookingFlowEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.CP_GRANT_ADVISORY_CALENDLY)
  const bookingUrl = useGrantAdvisoryBookingUrl('sidebar', GRANT_ADVISORY_BOOKING_TRACKING.sidebar)
  const [isBookingOpen, setIsBookingOpen] = useState(false)

  const trackingProperties = {
    organisation: authState.organisation?.displayName,
    organisationId: authState.organisation?.id,
    source: 'Sidebar',
    accountId: authState.user?.id
  }

  const handleGetInTouchClick = () => {
    track('Sidebar Grant Promo CTA clicked', trackingProperties)

    // Without a booking URL from PostHog there is nothing to book, so keep the
    // existing HubSpot form as the destination rather than opening a dead CTA.
    if (isBookingFlowEnabled && bookingUrl) {
      setIsBookingOpen(true)
    } else {
      window.open(SIDEBAR_GOODSTACK_PRO_PROMO_FORM_LINK, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <Box className={styles.promoWrapper}>
      <Box className={styles.promoCard}>
        <Flex gap={6} align="center">
          <Box className={styles.iconWrapper}>
            <Icon name="reward-stars" size={4} color="inherit" />
          </Box>
          <Typography variant="h6">{t('sidebarGrantPromo.heading')}</Typography>
        </Flex>
        <Flex direction="column" gap={4} mb={4}>
          <Typography variant="small" $color="gray400" className={styles.title}>
            {t('sidebarGrantPromo.title')}
          </Typography>
        </Flex>
        <Box className={styles.buttonWrapper}>
          <Button
            variant="primary"
            size="small"
            onClick={handleGetInTouchClick}
            data-testid="sidebar-grant-promo-cta-button"
          >
            {t('sidebarGrantPromo.cta')}
          </Button>
        </Box>
      </Box>
      {isBookingOpen && bookingUrl && (
        <GrantAdvisoryBookingModal
          entryPoint="sidebar"
          bookingUrl={bookingUrl}
          onClose={() => setIsBookingOpen(false)}
          onScheduled={() => track('Sidebar Grant Promo Meeting Scheduled', trackingProperties)}
          analyticsProperties={trackingProperties}
        />
      )}
    </Box>
  )
}
